import React, { useId, useState } from "react";
import { useSettings } from "@/hooks/useSettings";
import { useUnitInput } from "@/hooks/useUnitInput";
import type { Quantity } from "@/lib/units";

type UnitInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "onBlur"> & {
  /** Stored SI value (string). */
  value: string;
  /** Quantity to display in US units; omit for a plain pass-through field. */
  quantity?: Quantity;
  /** Receives the SI string (converted back from a US entry). */
  onValue: (v: string) => void;
  onBlur?: () => void;
};

/**
 * An <input> for an SI field shown in the user's display units. In SI it is
 * the same <input type="number"> as before; in US it becomes a text input
 * (inputMode decimal) so entries like "31 7/8" or "31⅞" can be typed. The
 * min/max attributes are SI bounds, so they are dropped in US — the hard
 * limits are still enforced by the SI validators (ganttErrors).
 */
export function UnitInput({ value, quantity, onValue, onBlur, type, min, max, step, ...rest }: UnitInputProps) {
  const { uiUnits } = useSettings();
  const u = useUnitInput(value, quantity, onValue, onBlur);
  const us = uiUnits === "US" && !!quantity;
  const kind = us
    ? { type: "text", inputMode: "decimal" as const }
    : { type, min, max, step };
  return (
    <input
      {...rest}
      {...kind}
      value={u.value}
      onChange={(e) => u.onChange(e.target.value)}
      onBlur={us || onBlur ? u.onBlur : undefined}
    />
  );
}

interface CardProps {
  title: React.ReactNode;
  summary?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

export function Card({ title, summary, children, defaultOpen = true }: CardProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card">
      <div
        className="hd"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(o => !o); }
        }}
      >
        <div className="hd-wrap">
          <div>{title}</div>
          {summary && (
            <div className="hd-sum" style={{ display: open ? "none" : "block" }}>
              {summary}
            </div>
          )}
        </div>
        <svg className={`hd-chev${open ? " up" : ""}`} width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.8"
            strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <div className={`card-bd${open ? "" : " closed"}`}>
        <div className="bd">{children}</div>
      </div>
    </div>
  );
}

interface FieldRowProps {
  label: string;
  unit: string;
  children: React.ReactNode;
  error?: string;
  /** Set when the value carries a sticky "!" soft-range override: shows a
   *  "!" chip that clears it when clicked. */
  override?: () => void;
  /** Informational note under the row (autocorrect / clamp feedback); hidden
   *  while there is an error. */
  note?: string;
}

/**
 * Label + control + unit row. When the child is an <input> / <select> it is
 * given an id the <label> points at, plus aria-invalid / aria-describedby
 * for the error message; output boxes get aria-labelledby instead.
 */
export function FieldRow({ label, unit, children, error, override, note }: FieldRowProps) {
  const id = useId();
  const errId = `${id}-err`;
  const child = React.Children.count(children) === 1 ? React.Children.toArray(children)[0] : children;
  const isControl = React.isValidElement(child)
    && (child.type === "input" || child.type === "select" || child.type === UnitInput);
  const control = React.isValidElement(child)
    ? React.cloneElement(child as React.ReactElement<any>, isControl
        ? { id, "aria-invalid": error ? true : undefined, "aria-describedby": error ? errId : undefined }
        : { "aria-labelledby": `${id}-lbl` })
    : child;
  return (
    <>
      <div className="fld">
        {isControl
          ? <label className="lbl" htmlFor={id}>{label}</label>
          : <span className="lbl" id={`${id}-lbl`}>{label}</span>}
        {override && (
          <button
            type="button"
            className="ovr-chip"
            onClick={override}
            title="Soft-range override (!) is on — click to clear it and apply the normal range"
            aria-label={`${label}: soft-range override on — clear override`}
          >
            !
          </button>
        )}
        {control}
        <span className="unit">{unit}</span>
      </div>
      {error && <div className="fld-err" id={errId}>{error}</div>}
      {!error && note && <div className="fld-note" role="status">{note}</div>}
    </>
  );
}
